import { Clipboard } from '@angular/cdk/clipboard';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Animations } from 'src/app/common/utilities/animations';
import { UploadStyles } from 'src/app/private/shared/components/file-management/file-upload/file-upload.component';
import { UploadType } from 'src/app/private/shared/enums/upload-type.enum';
import { FileDTO } from '../../dtos/file.dto';
import {
  AIUserFeedback,
  ChatMessage,
  Conversation,
  GlaiUserFeedbackPayload,
} from '../../dtos/glai.dto';
import { GlaiService } from '../../services/glai/glai.service';

@Component({
  selector: 'app-glai',
  templateUrl: './glai.component.html',
  styleUrl: './glai.component.scss',
  animations: [Animations.delayfadeInOut, Animations.fadeInOut],
  standalone: false,
})
export class GlaiComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesArea') private messagesArea!: ElementRef;

  @ViewChild('uploadComponent') uploadComponent: ElementRef;

  @ViewChild('textareaprompt') textareaprompt: ElementRef<HTMLTextAreaElement>;

  feedbackByMessageId: Record<string, AIUserFeedback> = {};

  @Output() readonly fileUploads = new EventEmitter<FileDTO>();

  uploadedFiles: FileDTO[] = [];

  uploadStyle = UploadStyles.ICON;

  modalUploadStyle = UploadStyles.SIMPLE;

  uploadType = UploadType.USER_UPLOAD;

  activeModes: string[] = ['default'];

  userMessage = '';

  messages: ChatMessage[] = [];

  currentConversation: Conversation | null = null;

  isLoading = false;

  error: string | null = null;

  private messageSubscription?: Subscription;

  selectedPath = 'Question & Answer';

  customCollapsed = false;

  AIUserFeedback = AIUserFeedback;

  fullscreen = false;

  currentClass = 'bi-chat-fill';

  @HostListener('document:keydown.escape', []) onKeydownHandler() {
    if (this.fullscreen) {
      this.toggleFullscreen();
    }
  }

  constructor(private glaiService: GlaiService, private clipboard: Clipboard) {}

  ngOnInit(): void {
    this.messageSubscription = this.glaiService.messages$.subscribe(
      (message: ChatMessage) => {
        const existingIndex = this.messages.findIndex(
          (m) => m.id === message.id
        );
        if (existingIndex >= 0) {
          this.messages[existingIndex] = message;
        } else {
          this.messages.push(message);
        }
        setTimeout(() => this.scrollToBottom(), 300);
      }
    );
  }

  ngAfterViewChecked(): void {
    this.handleResize();
  }

  toggleFullscreen() {
    this.fullscreen = !this.fullscreen;
    if (this.fullscreen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    }
  }

  resetConversation() {
    this.glaiService.disconnectStream();
    this.messages = [];
    this.currentConversation = null;
    this.error = null;
  }

  toggleMode(mode: string): void {
    switch (mode) {
      case 'default':
        this.selectedPath = 'Question & Answer';
        this.currentClass = 'bi-chat-fill';
        break;

      case 'lesson_planning':
        this.selectedPath = 'Lesson & Activity Planning';
        this.currentClass = 'bi-book-fill';
        break;

      case 'diff_and_mods':
        this.selectedPath = 'Differentiation and Modifications';
        this.currentClass = 'bi-people-fill';
        break;

      case 'assessments_and_feedback':
        this.selectedPath = 'Assessments and Feedback';
        this.currentClass = 'bi-clipboard-check-fill';
        break;

      case 'communication':
        this.selectedPath = 'Professional Communication';
        this.currentClass = 'bi-envelope-fill';
        break;

      case 'data_analysis':
        this.selectedPath = 'Data Analysis';
        this.currentClass = 'bi-bar-chart-fill';
        break;

      default:
        this.selectedPath = 'Question & Answer';
        this.currentClass = 'bi-chat-fill';
        break;
    }

    this.activeModes = [mode];
  }

  sendMessage(): void {
    if (!this.userMessage.trim() || this.isLoading) return;

    const message = this.userMessage.trim();
    this.userMessage = '';
    this.isLoading = true;

    // Add user message to the chat
    const messageFiles = [...this.uploadedFiles];
    this.glaiService.addUserMessage(message, messageFiles);

    if (!this.currentConversation) {
      this.startNewConversation(message, messageFiles);
    } else {
      this.continueConversation(message, messageFiles);
    }

    // remove files
    this.uploadedFiles = [];
  }

  private startNewConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    this.glaiService
      .startConversation(message, messageFiles, this.activeModes)
      .subscribe({
        next: (response) => {
          this.currentConversation = response.item;
          this.glaiService.connectToStream(response.item.glai_stream_id);
          this.isLoading = false;
          setTimeout(() => this.textareaprompt.nativeElement.focus(), 300);
        },
        error: (error) => {
          this.error = `Failed to start conversation: ${error}`;
          this.isLoading = false;
        },
      });
  }

  private continueConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    if (!this.currentConversation) return;

    this.glaiService
      .continueConversation(
        this.currentConversation.id,
        message,
        messageFiles,
        this.activeModes
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          setTimeout(() => this.textareaprompt.nativeElement.focus(), 300);
        },
        error: (error) => {
          this.error = `Failed to continue conversation: ${error}`;
          this.isLoading = false;
        },
      });
  }

  addToFileList(files: FileDTO): void {
    // add to uploadedFileIds
    this.uploadedFiles.push(files);
  }

  removeFromFileList(file: FileDTO) {
    // remove from uploadedFiles
    this.uploadedFiles = this.uploadedFiles.filter((f) => f.id !== file.id);
  }

  private scrollToBottom(): void {
    this.messagesArea.nativeElement.scrollTo({
      top: this.messagesArea.nativeElement.scrollHeight,
      behavior: 'smooth',
    });
  }

  private handleResize(): void {
    this.textareaprompt.nativeElement.style.height = 'auto';
    this.textareaprompt.nativeElement.style.height = `${this.textareaprompt.nativeElement.scrollHeight}px`;
    this.scrollToBottom();
  }

  // BEGIN FACILITATOR ONLY FEATURES
  copyMessage(code: string, $event: Event) {
    this.clipboard.copy(code);
    const target = $event.target as HTMLInputElement | null;
    target?.classList.remove('bi-copy');
    target?.classList.add('bi-check2');
    setTimeout(() => {
      target?.classList.remove('bi-check2');
      target?.classList.add('bi-copy');
    }, 2000);
  }

  submitUserFeedback(message: ChatMessage, feedback: AIUserFeedback) {
    if (this.currentConversation) {
      const payload: GlaiUserFeedbackPayload = {
        user_feedback: feedback,
        message_text: message.content,
        role: 'assistant',
      };
      this.glaiService
        .submitUserFeedback(this.currentConversation.id, payload)
        .subscribe({
          next: () => {
            this.feedbackByMessageId[message.id] = feedback;
          },
          error: (error) => {
            this.error = `Failed to submit feedback: ${error}`;
          },
        });
    }
  }

  isThumbsUpSelected(message: ChatMessage): boolean {
    return this.feedbackByMessageId[message.id] === AIUserFeedback.THUMBS_UP;
  }

  isThumbsDownSelected(message: ChatMessage): boolean {
    return this.feedbackByMessageId[message.id] === AIUserFeedback.THUMBS_DOWN;
  }

  ngOnDestroy(): void {
    this.messageSubscription?.unsubscribe();
    this.glaiService.disconnectStream();
  }
}
